// The shared inbox (brief 1B-3, PLAN.md 4.7): who a message is from, which record it belongs to,
// where it lands, what may go out by text, and the readiness reminders. Pure: no database, no
// clock (the time is handed in), no outside service.

import { DEFAULT_TIME_ZONE, zonedDay } from "./week";
import { normalizeMobile } from "./people";

export type InboxScreen =
  "overview" | "work" | "new" | "docs" | "flags" | "fyi" | "done" | "filtered";

export const INBOX_SCREENS: readonly InboxScreen[] = [
  "overview",
  "work",
  "new",
  "docs",
  "flags",
  "fyi",
  "done",
  "filtered",
];

export const isInboxScreen = (s: unknown): s is InboxScreen =>
  typeof s === "string" && (INBOX_SCREENS as readonly string[]).includes(s);

export type ItemKind = "work" | "new" | "flag" | "fyi" | "document";
export type ItemStatus = "open" | "done" | "filtered";

/** Which items a screen of the inbox lists (Documents lists the credentials waiting instead). */
export function onScreen(
  screen: InboxScreen,
  item: { kind: ItemKind; status: ItemStatus },
): boolean {
  switch (screen) {
    case "overview":
      return item.status === "open";
    case "work":
    case "new":
    case "fyi":
      return item.status === "open" && item.kind === screen;
    case "flags":
      return item.status === "open" && item.kind === "flag";
    case "docs":
      return item.status === "open" && item.kind === "document";
    case "done":
      return item.status === "done";
    case "filtered":
      return item.status === "filtered";
  }
}

/** The mockup's label colors (LBLC). */
export const LABEL_LEVEL: Readonly<Record<string, string>> = {
  "New referral": "info",
  "New client": "info",
  "New candidate": "info",
  Client: "ok",
  Caregiver: "ok",
  Driver: "ok",
  Person: "ok",
  "Not linked": "plain",
  "Document to review": "warn",
  Flag: "bad",
  Notification: "plain",
  Reminder: "plain",
  "Returned mail": "bad",
  "Fax failed": "bad",
  "Fax confirmation": "plain",
  // Brief 1B-6: a call nobody answered, and a connection that went wrong (told to each Admin).
  "Missed call": "warn",
  Connection: "bad",
};

// ---- Who it's from ------------------------------------------------------------------------------

/** An email address kept one way: lower case, no spaces. Null when it isn't one. */
export function normalizeEmail(raw: string | null | undefined): string | null {
  const e = (raw ?? "").trim().toLowerCase();
  return /^[^@\s<>]+@[^@\s<>]+\.[^@\s<>]+$/.test(e) ? e : null;
}

/** At most this many people on one email (brief 1E-15). */
export const MOST_RECIPIENTS = 20;

/**
 * The To of one email to several people (brief 1E-15): a list, or one line with commas (or
 * semicolons) between; "Dana Price <dana@example.com>" reads as the address. Each address is
 * checked, each kept once, in the order typed.
 */
export function readEmailList(
  raw: unknown,
): { ok: true; to: string[] } | { ok: false; error: string } {
  const parts = (Array.isArray(raw) ? raw : typeof raw === "string" ? raw.split(/[,;]/) : [])
    .map((p) => (typeof p === "string" ? p.trim() : ""))
    .filter(Boolean);
  if (!parts.length) return { ok: false, error: "Type the email address it goes to." };
  const to: string[] = [];
  for (const p of parts) {
    const e = normalizeEmail(/<([^>]+)>/.exec(p)?.[1] ?? p);
    if (!e) return { ok: false, error: `Check the email address: ${p.slice(0, 80)}.` };
    if (!to.includes(e)) to.push(e);
  }
  if (to.length > MOST_RECIPIENTS)
    return { ok: false, error: `One email goes to ${MOST_RECIPIENTS} people at most.` };
  return { ok: true, to };
}

/** A phone or fax number kept as 410-555-0123, whatever way it was typed. */
export const normalizeNumber = (raw: string | null | undefined): string | null =>
  raw ? normalizeMobile(raw) : null;

/** An email header's "Dana Price <dana@example.com>" as a name and an address. */
export function parseAddress(header: string): { name: string; address: string | null } {
  const m = /^\s*"?([^"<]*?)"?\s*<([^>]+)>\s*$/.exec(header);
  if (m) {
    const address = normalizeEmail(m[2]);
    return { name: (m[1] ?? "").trim() || (address ?? ""), address };
  }
  const address = normalizeEmail(header);
  return { name: address ?? header.trim(), address };
}

/**
 * A fax that came by email names the number it came from in its subject or first lines
 * ("New Fax Message from (410) 555-0101"). The first US number found, or null.
 */
export function faxNumberIn(text: string): string | null {
  const m = /(\+?1[\s.-]?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}/.exec(text);
  return m ? normalizeNumber(m[0]) : null;
}

// ---- Which record it belongs to -----------------------------------------------------------------

/** Who a company can match a sender against. */
export interface Directory {
  /** A client's contacts (not archived). */
  contacts: readonly {
    clientId: string;
    name: string;
    clientName: string;
    email: string | null;
    phone: string | null;
    fax: string | null;
  }[];
  people: readonly { id: string; name: string; email: string | null; mobile: string | null }[];
}

export type Match =
  | { kind: "client"; id: string; name: string }
  | { kind: "person"; id: string; name: string }
  | { kind: "none" };

/**
 * Links an incoming message only when its email, mobile or fax number matches exactly one record
 * of the company: a client (through one of its contacts) or a person. Two records with the same
 * address or number, or none, and it waits for a person to link it.
 */
export function matchSender(
  sender: { email?: string | null; number?: string | null; fax?: string | null },
  dir: Directory,
): Match {
  const email = normalizeEmail(sender.email);
  const number = normalizeNumber(sender.number);
  const fax = normalizeNumber(sender.fax);
  const found = new Map<string, Match>();
  for (const c of dir.contacts) {
    const hit =
      (email && normalizeEmail(c.email) === email) ||
      (number && normalizeNumber(c.phone) === number) ||
      (fax && normalizeNumber(c.fax) === fax);
    if (hit)
      found.set(`client:${c.clientId}`, {
        kind: "client",
        id: c.clientId,
        name: `${c.name} · ${c.clientName}`,
      });
  }
  for (const p of dir.people) {
    const hit =
      (email && normalizeEmail(p.email) === email) ||
      (number && normalizeNumber(p.mobile) === number);
    if (hit) found.set(`person:${p.id}`, { kind: "person", id: p.id, name: p.name });
  }
  return found.size === 1 ? ([...found.values()][0] as Match) : { kind: "none" };
}

/**
 * Where a message with no record goes, by a simple rule (no AI): one that mentions a referral is
 * a likely new client, one that mentions an application, applying or a job a likely candidate;
 * they go to New to us. Anything else waits in Needs action.
 */
export function newToUs(subject: string, body: string): "New referral" | "New candidate" | null {
  const text = `${subject}\n${body}`;
  if (/\breferr(al|als|ed|ing)\b/i.test(text)) return "New referral";
  if (/\b(application|applying|apply|applied|jobs?)\b/i.test(text)) return "New candidate";
  return null;
}

/** Where an incoming message lands and how it's labeled. With AI off, nothing is filtered out. */
export function sortIncoming(
  match: Match,
  subject: string,
  body: string,
  personLabel = "Person",
): { kind: "work" | "new"; label: string } {
  if (match.kind === "client") return { kind: "work", label: "Client" };
  if (match.kind === "person") return { kind: "work", label: personLabel };
  const fresh = newToUs(subject, body);
  return fresh ? { kind: "new", label: fresh } : { kind: "work", label: "Not linked" };
}

/** The first words of a message, on one line, for the list. */
export function preview(body: string, max = 140): string {
  const line = body.replace(/\s+/g, " ").trim();
  return line.length > max ? `${line.slice(0, max - 1).trimEnd()}…` : line;
}

/** A reply's subject: "Re: " once. */
export const replySubject = (subject: string) =>
  /^re:/i.test(subject.trim()) ? subject.trim() : `Re: ${subject.trim() || "(no subject)"}`;

// ---- What may go out -------------------------------------------------------------------------

/**
 * A text never carries a document or patient details (PLAN.md 6C: text a secure link only).
 * Refused: any attachment, and anything that looks like patient details (a date of birth, a
 * Social Security, Medicaid, Medicare or record number, a diagnosis or medication).
 */
export function refuseText(body: string, attachments: readonly unknown[] = []): string | null {
  if (attachments.length)
    return "A text can't carry a document. Send it by email, or leave the attachment off.";
  const patient = [
    /\b\d{3}-\d{2}-\d{4}\b/, // SSN
    /\b(dob|d\.o\.b\.|date of birth|born on)\b/i,
    /\b(ssn|social security|medicaid|medicare|mrn|medical record|member id)\b/i,
    /\b(diagnos[ie]s|diagnosed|dx|medication|prescription|wound|icd-?10)\b/i,
  ];
  if (patient.some((p) => p.test(body)))
    return "A text can't carry patient details. Say it by email, or ask them to call the office.";
  return null;
}

/** Reads a reply: words, and on email only, files to attach. Refuses in plain words. */
export function readReply(
  input: unknown,
  channel: string,
): { ok: true; body: string; attachments: string[] } | { ok: false; error: string } {
  const b = typeof input === "object" && input !== null ? (input as Record<string, unknown>) : {};
  const body = typeof b.body === "string" ? b.body.trim() : "";
  const attachments = Array.isArray(b.attachments)
    ? b.attachments.filter((a): a is string => typeof a === "string")
    : [];
  if (!body) return { ok: false, error: "Write a reply first." };
  if (body.length > (channel === "text" ? 1600 : 50000))
    return { ok: false, error: "That reply is too long." };
  if (channel === "text") {
    const refused = refuseText(body, attachments);
    if (refused) return { ok: false, error: refused };
  } else if (channel !== "email") {
    return {
      ok: false,
      error: "Replies go by email or text. Send a fax from the document's Send button.",
    };
  }
  return { ok: true, body, attachments };
}

// ---- Connected inboxes ---------------------------------------------------------------------------

export const PROVIDER_FOR = { email: "gmail", text: "twilio", fax: "ringcentral" } as const;
export const PROVIDER_LABEL = { gmail: "Gmail", twilio: "Twilio", ringcentral: "RingCentral" };

/**
 * Reads a mailbox, text number or fax sender to add to Connected inboxes: {channel, address,
 * goesTo}. A mailbox and the fax sender are email addresses; the text number is a US number.
 */
export function readConnectedInbox(
  input: unknown,
):
  | { ok: true; value: { channel: "email" | "text" | "fax"; address: string; goes_to: string } }
  | { ok: false; error: string } {
  const b = typeof input === "object" && input !== null ? (input as Record<string, unknown>) : {};
  const channel = b.channel;
  if (channel !== "email" && channel !== "text" && channel !== "fax")
    return { ok: false, error: "Pick a mailbox, the text number or the fax sender." };
  const raw = typeof b.address === "string" ? b.address : "";
  const address = channel === "text" ? normalizeNumber(raw) : normalizeEmail(raw);
  if (!address)
    return {
      ok: false,
      error: channel === "text" ? "Check the text number." : "Check the email address.",
    };
  const goes = typeof b.goesTo === "string" ? b.goesTo.trim() : "";
  if (goes.length > 120) return { ok: false, error: "Keep “Goes to” under 120 characters." };
  return { ok: true, value: { channel, address, goes_to: goes } };
}

// ---- Readiness reminders -------------------------------------------------------------------------

/**
 * How a person gets a reminder: a text to their mobile, or else an email. Null when they have
 * neither.
 */
export function reminderChannel(person: {
  mobile: string | null;
  email: string | null;
}): { channel: "text"; to: string } | { channel: "email"; to: string } | null {
  if (person.mobile) return { channel: "text", to: person.mobile };
  const email = normalizeEmail(person.email);
  return email ? { channel: "email", to: email } : null;
}

// ---- Time, as the list shows it ----------------------------------------------------------------

/**
 * "10:42 AM" today, "Yesterday", the weekday within a week, else "10/14/2026", on the company's
 * clock (brief 1E-8).
 */
export function shortWhen(at: Date, now: Date, zone = DEFAULT_TIME_ZONE): string {
  const day = zonedDay(at, zone);
  if (day === zonedDay(now, zone))
    return new Intl.DateTimeFormat("en-US", {
      timeZone: zone,
      hour: "numeric",
      minute: "2-digit",
    }).format(at);
  if (day === zonedDay(new Date(now.getTime() - 86_400_000), zone)) return "Yesterday";
  if (now.getTime() - at.getTime() < 6 * 86_400_000)
    return new Intl.DateTimeFormat("en-US", { timeZone: zone, weekday: "short" }).format(at);
  return new Intl.DateTimeFormat("en-US", {
    timeZone: zone,
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  }).format(at);
}
